import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DeleteModalWithConfirmInput } from '@/lib/Modals';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useDeleteCampaignV2Mutation } from '@/services/endpoints/campaigns';
import { CampaignResponse, CampaignV2Response } from '@/services/endpoints/campaigns/types';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

type DeleteCampaignModalProps = {
  campaign?: CampaignResponse | CampaignV2Response;
  closeModal: VoidFunction;
  /** When set, navigates here after delete instead of the default campaigns list route. */
  redirectAfterDelete?: string;
};

export const DeleteCampaignModal: FC<DeleteCampaignModalProps> = ({ campaign, closeModal, redirectAfterDelete }) => {
  const { t } = useTranslation('campaign');

  const workspace = useWorkspace();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [deleteCampaign, { isLoading }] = useDeleteCampaignV2Mutation();

  const handleDeleteCampaign = async () => {
    if (!workspace.id || !campaign?.id) return;

    try {
      await deleteCampaign({
        workspaceId: workspace.id,
        campaignId: campaign.id,
      }).unwrap();

      closeModal();
      history.push(redirectAfterDelete ?? RouteConstants.campaigns);

      dispatch(
        showToast({
          type: ToastType.SUCCESS,
          title: t('campaignDeleted'),
          message: t('campaignRemovedSuccessfully', { campaignName: campaign?.name }),
        })
      );
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('campaignDeletionFailed'));
      dispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
    }
  };

  return (
    <DeleteModalWithConfirmInput
      inputComparisonValue={campaign.name}
      confirmText={'campaign:deleteThisCampaign'}
      onConfirm={handleDeleteCampaign}
      onClose={closeModal}
      isLoading={isLoading}
    >
      <DeleteModalWithConfirmInput.Title>{t('deleteThisCampaign')}</DeleteModalWithConfirmInput.Title>
      <DeleteModalWithConfirmInput.Description>{t('deleteCampaignWarning')}</DeleteModalWithConfirmInput.Description>

      <DeleteModalWithConfirmInput.InputHint>
        <Trans i18nKey={'campaign:deleteCampaignConfirmation'}>
          To confirm the deletion, write here the name of the audience <strong>{{ name: campaign.name }}</strong>
        </Trans>
      </DeleteModalWithConfirmInput.InputHint>
    </DeleteModalWithConfirmInput>
  );
};
